import { useEffect, useState } from "react";
import { Pause, Play, SkipForward, Phone, CheckSquare, Square, Loader, ClipboardList, Calendar, MessageSquare, Mail, FileText, Activity as ActIcon, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";


const TYPE_ICONS = {
  email: Mail, task: ClipboardList,
  reminder: Calendar, document_sent: FileText, document_generated: FileText,
  stage_change: ActIcon, manual: Phone, checklist: ClipboardList,
  ai: ActIcon, alert: Calendar,
};


function _date(iso) {
  if (!iso) return "";
  try { return fmtDateTime(iso); } catch { return iso; }
}


export default function FollowUpCadencePanel({ opp }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logForm, setLogForm] = useState({ open: false, type: "phone", label: "", notes: "" });
  const [timelineOpen, setTimelineOpen] = useState(false);

  const load = async () => {
    if (!opp?.oid) return;
    try {
      const r = await api.get(`/cadence/${opp.oid}`);
      setData(r.data);
    } catch {/* non-fatal */}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [opp?.oid]);

  if (!opp) return null;

  // Only show for stages 3-7 per spec
  const ACTIVE_STAGES = ["outreach_sent", "engaged", "offer_stage", "under_loi", "due_diligence"];
  if (!ACTIVE_STAGES.includes(opp.cf_stage)) {
    return null;
  }

  if (!data) {
    return <div className="ae-card p-5 text-sm text-slate-500" data-testid="cadence-loading">Loading follow-up cadence...</div>;
  }

  const action = async (path, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/cadence/${opp.oid}/${path}`, body);
      toast.success("Cadence updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const togglePause = () => action(data.paused ? "resume" : "pause");
  const skipNext = () => {
    const next = data.scheduled[0];
    if (!next) { toast.info("No scheduled item"); return; }
    action("skip-next", { key: next.key });
  };

  const submitManual = async () => {
    if (!logForm.label.trim()) { toast.error("Add a label"); return; }
    await action("log-manual", { type: logForm.type, label: logForm.label, notes: logForm.notes });
    setLogForm({ open: false, type: "phone", label: "", notes: "" });
  };

  const toggleDD = (item) => action("dd-toggle", { item });

  return (
    <div className="ae-card p-5 space-y-4" data-testid="cadence-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-emerald-400" />
            <h3 className="font-heading text-lg">Follow-Up Cadence</h3>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Automated touchpoints based on the current stage. Pause to take manual control or log a phone call below.
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={togglePause} disabled={busy} data-testid="cadence-pause-toggle"
                  className="btn-ghost text-xs inline-flex items-center gap-1">
            {data.paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
            {data.paused ? "Resume" : "Pause"}
          </button>
          <button onClick={skipNext} disabled={busy || !data.scheduled.length} data-testid="cadence-skip-next"
                  className="btn-ghost text-xs inline-flex items-center gap-1">
            <SkipForward className="h-3 w-3" /> Skip next
          </button>
          <button onClick={() => setLogForm({ ...logForm, open: !logForm.open })} data-testid="cadence-log-manual-toggle"
                  className="px-2 py-1 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 inline-flex items-center gap-1">
            <Phone className="h-3 w-3" /> Log manual contact
          </button>
        </div>
      </div>

      {data.paused && (
        <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-sm p-2"
             data-testid="cadence-paused-banner">
          Cadence is paused. Automated touchpoints will not fire until you click Resume.
        </div>
      )}

      {logForm.open && (
        <div className="border border-white/10 rounded-sm bg-white/3 p-3 space-y-2" data-testid="cadence-manual-form">
          <select value={logForm.type} onChange={(e) => setLogForm({ ...logForm, type: e.target.value })}
                  data-testid="cadence-manual-type"
                  className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-xs text-slate-200">
            <option value="phone">Phone call</option>
            <option value="meeting">In-person meeting</option>
            <option value="other">Other</option>
          </select>
          <input value={logForm.label} onChange={(e) => setLogForm({ ...logForm, label: e.target.value })}
                 placeholder="Short label (e.g. 'Called owner, left voicemail')" data-testid="cadence-manual-label"
                 className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-xs text-slate-200" />
          <textarea value={logForm.notes} onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
                    placeholder="Notes (optional)" rows={3} data-testid="cadence-manual-notes"
                    className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-xs text-slate-200" />
          <div className="flex gap-2">
            <button onClick={submitManual} disabled={busy} data-testid="cadence-manual-save"
                    className="px-3 py-1 text-[10px] uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25">Save</button>
            <button onClick={() => setLogForm({ ...logForm, open: false })} className="btn-ghost text-[10px]">Cancel</button>
          </div>
        </div>
      )}

      {/* DD checklist */}
      {data.dd_checklist?.length > 0 && (
        <div className="space-y-1.5" data-testid="cadence-dd-checklist">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Due diligence checklist</div>
          {data.dd_checklist.map((c) => {
            const Icon = c.completed ? CheckSquare : Square;
            return (
              <button key={c.item} onClick={() => toggleDD(c.item)} disabled={busy}
                      data-testid={`dd-item-${c.item.slice(0, 20).replace(/\s/g, "-")}`}
                      className="w-full flex items-center gap-2 text-left text-sm text-slate-200 hover:bg-white/5 px-2 py-1 rounded-sm">
                <Icon className={`h-3.5 w-3.5 ${c.completed ? "text-emerald-400" : "text-slate-500"}`} />
                <span className={c.completed ? "line-through text-slate-500" : ""}>{c.item}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Scheduled */}
      {data.scheduled?.length > 0 && (
        <div className="space-y-1" data-testid="cadence-scheduled">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Scheduled</div>
          {data.scheduled.map((s, i) => {
            const Icon = TYPE_ICONS[s.type] || ActIcon;
            return (
              <div key={i} className="flex items-baseline gap-3 text-xs py-1">
                <span className="font-mono text-amber-300 tabular-nums shrink-0 w-12">Day {s.day_offset}</span>
                <Icon className="h-3 w-3 text-slate-500 shrink-0" />
                <span className="text-slate-300 flex-1">{s.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Scheduled</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Timeline (collapsed by default) */}
      {data.timeline?.length > 0 && (
        <div className="pt-3 border-t border-white/8" data-testid="cadence-timeline">
          <button
            onClick={() => setTimelineOpen((v) => !v)}
            data-testid="cadence-timeline-toggle"
            className="w-full flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-200 py-1"
          >
            {timelineOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            <span>Timeline ({data.timeline.length})</span>
            <span className="ml-auto normal-case tracking-normal text-slate-500">
              {timelineOpen ? "Hide" : "Show"}
            </span>
          </button>
          {timelineOpen && (
            <div className="space-y-1.5 mt-2" data-testid="cadence-timeline-list">
              {data.timeline.slice(0, 40).map((t, i) => {
                const Icon = TYPE_ICONS[t.type] || ActIcon;
                const isDoc = (t.type === "document_sent" || t.type === "document_generated") && t.doc_created_at;
                const viewDoc = () => {
                  if (!isDoc) return;
                  const token = localStorage.getItem("ae_token") || "";
                  const params = new URLSearchParams({
                    oid: t.doc_oid || opp.oid,
                    created_at: t.doc_created_at,
                  });
                  // Fetch as blob and open in new tab (auth header can't be passed via <a>)
                  fetch(`${API}/documents/generated/view?${params}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                  })
                    .then((r) => {
                      if (!r.ok) throw new Error(`HTTP ${r.status}`);
                      return r.blob();
                    })
                    .then((blob) => {
                      const url = URL.createObjectURL(blob);
                      window.open(url, "_blank", "noopener,noreferrer");
                      // Revoke after a delay so the new tab has time to load
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                    })
                    .catch(() => toast.error("Unable to open document"));
                };
                const rowBase = "flex items-baseline gap-3 text-xs py-1.5 border-b border-white/3 last:border-0";
                const clickable = isDoc
                  ? "cursor-pointer hover:bg-white/5 rounded-sm px-1 -mx-1 group"
                  : "";
                return (
                  <div
                    key={i}
                    className={`${rowBase} ${clickable}`}
                    onClick={isDoc ? viewDoc : undefined}
                    role={isDoc ? "button" : undefined}
                    tabIndex={isDoc ? 0 : undefined}
                    onKeyDown={isDoc ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); viewDoc(); } } : undefined}
                    data-testid={isDoc ? `cadence-timeline-doc-${i}` : `cadence-timeline-row-${i}`}
                    title={isDoc ? "Click to view the document" : undefined}
                  >
                    <span className="font-mono text-slate-500 tabular-nums shrink-0 w-32">{_date(t.at).slice(0, 16)}</span>
                    <Icon className={`h-3 w-3 shrink-0 self-center ${isDoc ? "text-emerald-300 group-hover:text-emerald-200" : "text-emerald-400/70"}`} />
                    <span className={`flex-1 truncate ${isDoc ? "text-emerald-200 group-hover:underline" : "text-slate-300"}`}>
                      {t.label}
                      {isDoc && t.doc_format && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-widest text-slate-500 font-mono">
                          {t.doc_format}
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-mono">{t.status}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
